import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, apiClient } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { 
  MapPin, Clock, CheckCircle2, AlertCircle, Navigation, Phone,
  Calendar, ExternalLink, PlayCircle, CheckSquare
} from "lucide-react";

const statusColors = {
  pending: "bg-slate-100 text-slate-700",
  dispatched: "bg-blue-100 text-blue-700",
  en_route: "bg-amber-100 text-amber-700",
  on_site: "bg-purple-100 text-purple-700",
  completed: "bg-emerald-100 text-emerald-700",
  cancelled: "bg-red-100 text-red-700",
};

const statusLabels = {
  pending: "Pending",
  dispatched: "Dispatched",
  en_route: "En Route",
  on_site: "On Site",
  completed: "Completed",
  cancelled: "Cancelled",
};

const priorityColors = {
  low: "bg-slate-100 text-slate-700",
  medium: "bg-blue-100 text-blue-700",
  high: "bg-amber-100 text-amber-700",
  urgent: "bg-red-100 text-red-700",
};

export default function FieldAgentDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("active");

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, ordersRes] = await Promise.all([
        apiClient.get("/stats/dashboard"),
        apiClient.get("/work-orders"),
      ]);
      setStats(statsRes.data);
      setWorkOrders(ordersRes.data);
    } catch (error) {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const updateStatus = async (orderId, newStatus) => {
    try {
      await apiClient.patch(`/work-orders/${orderId}`, { status: newStatus });
      toast.success(`Status updated to ${statusLabels[newStatus]}`);
      fetchData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const filteredOrders = workOrders.filter((order) => {
    if (filter === "active") return !["completed", "cancelled"].includes(order.status);
    if (filter === "completed") return order.status === "completed";
    return true;
  });

  const StatCard = ({ icon: Icon, label, value, color }) => (
    <Card className="bg-white rounded-xl shadow-sm border border-slate-100">
      <CardContent className="p-5">
        <div className="flex items-center gap-4">
          <div className={`p-3 rounded-xl ${color}`}>
            <Icon className="h-6 w-6" />
          </div>
          <div>
            <p className="text-sm text-slate-500 font-medium">{label}</p>
            <p className="text-2xl font-bold font-heading text-slate-900">{value || 0}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const getNextStatus = (currentStatus) => {
    const flow = {
      pending: "dispatched",
      dispatched: "en_route",
      en_route: "on_site",
      on_site: "completed",
    };
    return flow[currentStatus];
  };

  const getActionLabel = (status) => {
    const labels = {
      pending: "Accept Job",
      dispatched: "Start Route",
      en_route: "Arrive On Site",
      on_site: "Complete Job",
    };
    return labels[status];
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="field-agent-dashboard">
        {/* Header - Mobile Friendly */}
        <div className="bg-gradient-to-r from-sag-navy to-sag-navy-800 rounded-xl p-6 text-white">
          <h1 className="font-heading text-2xl font-bold">Field Service</h1>
          <p className="text-white/80 mt-1">Welcome back, {user?.full_name}</p>
        </div>

        {/* Stats - Horizontal scroll on mobile */}
        <div className="flex gap-4 overflow-x-auto pb-2 -mx-4 px-4 md:mx-0 md:px-0 md:grid md:grid-cols-3">
          <div className="min-w-[200px] md:min-w-0">
            <StatCard icon={MapPin} label="Assigned Jobs" value={stats.assigned_jobs} color="bg-sag-navy/10 text-sag-navy" />
          </div>
          <div className="min-w-[200px] md:min-w-0">
            <StatCard icon={AlertCircle} label="Pending" value={stats.pending_jobs} color="bg-amber-100 text-amber-600" />
          </div>
          <div className="min-w-[200px] md:min-w-0">
            <StatCard icon={CheckCircle2} label="Completed" value={stats.completed_jobs} color="bg-emerald-100 text-emerald-600" />
          </div>
        </div>

        {/* Filter */}
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-lg font-semibold text-slate-900">Work Orders</h2>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[140px]" data-testid="work-order-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Work Orders List - Mobile Card Style */}
        {loading ? (
          <div className="text-center py-12 text-slate-500">Loading work orders...</div>
        ) : filteredOrders.length === 0 ? (
          <Card className="bg-white rounded-xl shadow-sm border border-slate-100">
            <CardContent className="p-8 text-center">
              <MapPin className="h-12 w-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">No work orders found</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredOrders.map((order) => (
              <Card 
                key={order.id} 
                className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden"
                data-testid={`work-order-card-${order.id}`}
              >
                {/* Priority Bar */}
                <div className={`h-1 ${order.priority === "urgent" ? "bg-red-500" : order.priority === "high" ? "bg-amber-500" : "bg-sag-blue"}`} />
                
                <CardContent className="p-5">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-slate-900">{order.title}</h3>
                      <p className="text-sm text-slate-500">{order.client_name}</p>
                    </div>
                    <Badge className={statusColors[order.status]} variant="secondary">
                      {statusLabels[order.status]}
                    </Badge>
                  </div>

                  {/* Details */}
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <MapPin className="h-4 w-4 text-slate-400" />
                      <span className="truncate">{order.location_address}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Calendar className="h-4 w-4 text-slate-400" />
                      <span>{new Date(order.scheduled_date).toLocaleDateString()}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Clock className="h-4 w-4 text-slate-400" />
                      <span>Est. {order.estimated_duration_hours}h</span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3">
                    {order.status !== "completed" && order.status !== "cancelled" && (
                      <Button
                        className="flex-1 h-12 bg-sag-navy hover:bg-sag-navy-800 text-white font-semibold"
                        onClick={() => updateStatus(order.id, getNextStatus(order.status))}
                        data-testid={`update-status-${order.id}`}
                      >
                        {order.status === "pending" && <PlayCircle className="h-4 w-4 mr-2" />}
                        {order.status === "dispatched" && <Navigation className="h-4 w-4 mr-2" />}
                        {order.status === "en_route" && <MapPin className="h-4 w-4 mr-2" />}
                        {order.status === "on_site" && <CheckSquare className="h-4 w-4 mr-2" />}
                        {getActionLabel(order.status)}
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      className="h-12"
                      onClick={() => navigate(`/work-orders/${order.id}`)}
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
